import React, { useEffect, useMemo, useRef, useState } from 'react';
import Loading from '../feedback/Loading';
import toast from 'react-hot-toast';
import utils from '../../utils';
import { HiTrash } from 'react-icons/hi';
import { useAuth } from '../../context/UserContext';
import vendorServices from '../../services/VendorSevices';
import { FaCheck } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import moment from 'moment';
import adminServices from '../../services/AdminServices';

function StoreModal({ action, activeStore, getStores }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [logo, setLogo] = useState(null);
    const [verificationDocuments, setVerificationDocuments] = useState(null);
    const [isVerified, setIsVerified] = useState(false);
    const [is_active, setIsActive] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const logoInputRef = useRef(null);
    const verificationInputRef = useRef(null);

    const { setStore, user } = useAuth();

    const tabs = useMemo(() => {
        const baseTabs = ["Info"];
        if (action === 1) {
            return [...baseTabs, "Delete"];
        }
        return baseTabs;
    }, [action]);

    // Initialize form for update
    useEffect(() => {
        if (action === 1 && activeStore) {
            setName(activeStore.name || '');
            // setTagline(activeStore.tagline || '');
            setDescription(activeStore.description || '');
            setLocation(activeStore.location || '');
            setIsVerified(!!activeStore.is_verified);
            setIsActive(!!activeStore.is_active);
        } else {
            setName('');
            setDescription('');
            setLocation('');
            setIsVerified(false);
            setIsActive(true);
            setLogo(null);
            setVerificationDocuments(null);
            if (logoInputRef.current) logoInputRef.current.value = '';
            if (verificationInputRef.current) verificationInputRef.current.value = '';
        }
    }, [action, activeStore]);

    // File handlers
    const handleFileChange = (setter, acceptableTypes = 'image/*') => (e) => {
        const file = e.target.files[0];
        if (file) {
            if (acceptableTypes === 'image/*' && !file.type.startsWith('image/')) {
                toast.error('Please select a valid image file.');
                e.target.value = '';
                return;
            }
            setter(file);
        }
    };

    const updateSessionStore = async () => {
        vendorServices.getVendorStore().then(
            (res) => {
                setStore(res.data[0]);
            },
            (err) => {
                console.warn('Failed to refresh store in session', err);
            }
        );
    };

    const submitHandler = (e) => {
        e.preventDefault();
        const closeBtn = document.getElementById('store-modal-close-btn');
        if (!closeBtn) return;

        setLoading(true);
        const formData = new FormData();

        formData.append('name', name.trim());

        description && formData.append('description', description);
        location && formData.append('location', location);

        formData.append('is_active', is_active);

        if (action === 0 && user?.is_vendor) {
            formData.append('vendor', user?.id);
        }

        if (logo) formData.append('logo', logo);
        if (verificationDocuments) formData.append('verification_documents', verificationDocuments);

        let apiCall;

        if (user?.is_vendor) {
            apiCall = action === 1
                ? vendorServices.updateStore(formData)
                : vendorServices.createStore(formData);
        } else if (user?.is_admin) {
            formData.append('is_verified', isVerified);

            apiCall = adminServices.updateStore(activeStore?.id, formData);
        }

        apiCall
            .then((res) => {
                const msg = action === 1 ? 'Store updated' : 'Store created';
                toast.success(`${msg} successfully!`);
                setLoading(false);
                if (user?.is_vendor) {
                    updateSessionStore();
                }
                if (user?.is_admin) {
                    getStores()
                }
                closeBtn.click();
            })
            .catch((err) => {
                console.error(err);
                toast.error(utils.toastError || 'Operation failed.');
                setLoading(false);
            });
    };

    const deleteHandler = () => {
        if (!activeStore?.id) return;

        const closeBtn = document.getElementById('store-modal-close-btn');
        setDeleteLoading(true);

        let apiCall

        if (user?.is_vendor) {
            apiCall = vendorServices
        }
        if (user?.is_admin) {
            apiCall = adminServices
        }

        apiCall
            .deleteStore(activeStore.id)
            .then(() => {
                toast.success('Store deleted successfully.');
                setDeleteLoading(false);
                setStore(null)
                closeBtn?.click();
            })
            .catch((err) => {
                console.error(err);
                toast.error(utils.toastError || 'Failed to delete store.');
                setDeleteLoading(false);
            });
    };

    function changeTab(idx) {
        if (action === 1) {
            setActiveTab(idx)
        }
    }

    return (
        <div>
            <label htmlFor="store-modal" id="store-open-btn" className="hidden">
                Manage Store
            </label>

            <input type="checkbox" id="store-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-11/12 max-w-5xl">
                    <label
                        htmlFor="store-modal"
                        id="store-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        onClick={() => {
                            // Reset form on close (optional)
                        }}
                    >
                        ✕
                    </label>

                    <h2 className="text-xl font-bold mb-4">
                        {action === 1 ? 'Update' : 'Create'} {user?.is_vendor && 'Your'} Store {action === 1 && activeStore && (<span className='ml-2 text-gray-500'>{activeStore?.name}</span>)}
                    </h2>

                    <div className="flex justify-center mb-6">
                        <div className=" border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {activeTab === 0 && (

                        <>
                            {action === 1 && activeStore?.logo && (
                                <div className={`mb-4 grid grid-cols-1 gap-3 ${user?.is_admin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                                    <div className={user?.is_vendor ? 'flex justify-center' : ''}>
                                        <img
                                            src={user?.is_vendor ? utils.baseUrl + activeStore.logo : activeStore.logo}
                                            alt={activeStore.name}
                                            className="rounded-lg max-h-48 object-contain"
                                        />
                                    </div>
                                    <div className='border p-3 rounded-lg shadow-md'>
                                        <p className="font-bold mb-1">Overview</p>
                                        <span className='flex gap-3 items-center'>
                                            Is Active:
                                            <div>
                                                {activeStore?.is_active ? (
                                                    <FaCheck />
                                                ) : (
                                                    <FaX />
                                                )}
                                            </div>
                                        </span>
                                        <span className='flex gap-3 items-center'>
                                            Is Verified:
                                            <div>
                                                {activeStore?.is_verified ? (
                                                    <FaCheck />
                                                ) : (
                                                    <FaX />
                                                )}
                                            </div>
                                        </span>
                                        <p>Product Count: <span className='font-bold ml-3 text-gray-600'>{activeStore?.product_count || 0}</span></p>
                                        <p>Total Orders: <span className='font-bold ml-3 text-gray-600'>{activeStore?.total_orders || 0}</span></p>
                                        <p>Total Revenue: <span className='font-bold ml-3 text-gray-600'>{activeStore?.total_revenue || 0}</span></p>
                                        <p>Created On: <span className='font-bold ml-3 text-gray-600'>{moment(activeStore?.creation_time).format("MMM Do YYYY")}</span></p>
                                    </div>
                                    {user?.is_admin && (
                                        <div className='border p-3 rounded-lg shadow-md'>
                                            <p className="font-bold mb-1">Vendor</p>
                                            <div className="flex items-center border-gray-200 gap-3 mb-4">
                                                <div className="avatar placeholder">
                                                    <div className="bg-neutral text-neutral-content w-14 rounded-full">
                                                        <span className="text-3xl">{utils.makeProfileLetter(activeStore?.vendor)}</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    {activeStore?.vendor?.full_name && (
                                                        <h3 className="font-bold">{activeStore?.vendor?.full_name}</h3>
                                                    )}
                                                    <p className='text-gray-600'>{activeStore?.vendor?.email}</p>
                                                </div>
                                            </div>
                                            <span className='flex gap-3 items-center'>
                                                Is Verified:
                                                <div>
                                                    {activeStore?.is_verified ? (
                                                        <FaCheck />
                                                    ) : (
                                                        <FaX />
                                                    )}
                                                </div>
                                            </span>
                                            <p>Joined: <span className='font-bold ml-3 text-gray-600'>{moment(activeStore?.vendor?.creation_time).format("MMM Do YYYY")}</span></p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <form onSubmit={submitHandler} className="space-y-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Store Name *</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        className="input input-bordered"
                                    />

                                </div>

                                {/* <div className="form-control">
                            <label className="label">
                                <span className="label-text">Tagline (e.g., "Quality electronics since 2020")</span>
                            </label>
                            <input
                                type="text"
                                value={tagline}
                                onChange={(e) => setTagline(e.target.value)}
                                maxLength={140}
                                className="input input-bordered"
                            />
                        </div> */}

                                {/* Location */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Location</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                        className="input input-bordered"
                                    />
                                </div>

                                {/* Description */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Description</span>
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows="3"
                                        className="textarea textarea-bordered"
                                    />
                                </div>

                                {/* Logo */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            {action === 0 ? 'Image *' : 'Image (Optional)'}
                                        </span>
                                    </label>
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange(setLogo)}
                                        required={action === 0}
                                        className="file-input file-input-bordered w-full"
                                    />
                                    {action === 1 && activeStore?.logo && (
                                        <div className="text-xs mt-1 text-gray-500">
                                            Current: {activeStore.logo.split('/').pop()}
                                        </div>
                                    )}
                                </div>

                                {/* Banner */}
                                {/* <div className="form-control">
                            <label className="label">
                                <span className="label-text">Banner (Optional)</span>
                            </label>
                            <input
                                ref={bannerInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange(setBanner)}
                                className="file-input file-input-bordered w-full"
                            />
                            {action === 1 && activeStore?.banner && (
                                <div className="text-xs mt-1 text-gray-500">
                                    Current: {activeStore.banner.split('/').pop()}
                                </div>
                            )}
                        </div> */}

                                {/* Verification Documents */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Verification Documents (e.g., Business License)</span>
                                    </label>
                                    <input
                                        ref={verificationInputRef}
                                        type="file"
                                        accept=".pdf,.doc,.docx,.jpg,.png"
                                        onChange={handleFileChange(setVerificationDocuments, 'any')}
                                        className="file-input file-input-bordered w-full"
                                    />
                                    {action === 1 && activeStore?.verification_documents && (
                                        <div className="text-xs mt-1 text-gray-500">
                                            Uploaded: {activeStore.verification_documents.split('/').pop()}
                                        </div>
                                    )}
                                </div>

                                {/* Status Toggles (Vendors can toggle `is_active`; `is_verified` is read-only) */}
                                <div className="flex flex-wrap gap-4 pt-2">
                                    <label className="cursor-pointer label">
                                        <span className="label-text mr-2">Active</span>
                                        <input
                                            type="checkbox"
                                            checked={is_active}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                            className="toggle toggle-primary"
                                        />
                                    </label>

                                    {action === 1 && (
                                        <div className="label">
                                            <span className="label-text">
                                                Verified: {user?.is_vendor && <strong>{isVerified ? 'Yes' : 'No'}</strong>}
                                            </span>
                                            {user?.is_admin && (
                                                <input
                                                    type="checkbox"
                                                    checked={isVerified}
                                                    onChange={(e) => setIsVerified(e.target.checked)}
                                                    className="toggle toggle-primary"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full btn btn-primary"
                                    >
                                        {loading ? (
                                            <span>{action === 0 ? 'Creating...' : 'Updating...'}</span>
                                        ) : (
                                            <span>{action === 0 ? 'Create Store' : 'Update Store'}</span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}


                    {activeTab === 1 && (

                        <>
                            {/* Delete Section (Update Mode Only) */}
                            {action === 1 && activeStore && (
                                <div className="border-base-200">
                                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden">
                                        <div className="p-6 md:p-8">
                                            <div className="border-l-4 border-error pl-4 py-2">
                                                <h3 className="font-bold text-error">Warning</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                    Deleting your store is irreversible. All products and orders will be affected.
                                                </p>
                                                <div className="pt-4">
                                                    {deleteLoading ? (
                                                        <Loading />
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={deleteHandler}
                                                            className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                                        >
                                                            <HiTrash className="h-4 w-4" />
                                                            <span>Delete Store</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}

export default StoreModal;